<script setup lang="ts">
import { PriceTypeMode } from '@enums/priceTypeMode.ts';
import OfferCard from '@components/OfferCard/OfferCard.vue';
import FavoriteButton from '@components/FavoriteButton.vue';
import type { AlgoliaOffer, ExtendedAlgoliaOffer } from '@global-types/algoliaOffer.ts';
import { useSearchStore } from '@stores/useSearchStore.ts';
import {
  type DeserializedOffer,
  getDeserializedOffer,
  getTotalMonthlyPrice,
} from '@pages/search/serializer/deserializedOffer';
import OfferPrice from '@components/OfferCard/OfferPrice.vue';
import Typography from '@components/Typography.vue';
import { computed, ref, watch } from 'vue';
import { IconCaretLeft, IconCaretRight, IconX } from '@icons';
import Button from '@components/Button.vue';
import Icon from '@components/Icon.vue';
import PromoteType from '@components/OfferCard/PromoteType.vue';
import { currencyNoDecimal } from '@tools/number-formatter';

const { offer } = defineProps<{ offer: ExtendedAlgoliaOffer }>();
const searchStore = useSearchStore();

const emit = defineEmits<{(e: 'close'): void }>();

const currentOffer = ref<AlgoliaOffer>(offer);
const currentOfferIndex = ref<number>(0);

const {
  addFavorite,
  removeFavorite,
  isFavorite,
  getAggregatedOfferUrl,
} = searchStore;

const onFavoriteChange = async (value: boolean): Promise<void> => {
  value ? addFavorite(currentOffer.value.hash_id) : removeFavorite(currentOffer.value.hash_id);
};

const deserializedOffer = computed<DeserializedOffer>(() => getDeserializedOffer(currentOffer.value));

const isGroupedOffer = computed<boolean>(() => (offer.grouped_offers?.length ?? 0) > 0);

const groupedOffers = computed<AlgoliaOffer[]>(() => (isGroupedOffer.value && offer.grouped_offers
  ? [ offer, ...offer.grouped_offers ]
  : [ offer ]));

const maxIndex = computed<number>(() => Math.max(0, groupedOffers.value.length - 1));

const maxScrollIndex = computed<number>(() => Math.max(0, groupedOffers.value.length - 2));

const scrollIndex = computed<number>(() => Math.min(currentOfferIndex.value, maxScrollIndex.value));

const prevSlide = (): void => {
  if (currentOfferIndex.value <= 0) {
    return;
  }

  currentOfferIndex.value -= 1;
};

const nextSlide = (): void => {
  if (currentOfferIndex.value >= maxIndex.value) {
    return;
  }

  currentOfferIndex.value += 1;
};

watch(() => offer, (newOffer) => {
  currentOffer.value = newOffer;
  currentOfferIndex.value = 0;
});

watch(currentOfferIndex, (newIndex) => {
  currentOffer.value = groupedOffers.value[newIndex];
});
</script>

<template>
  <div class="search-map-wrapper">
    <div
      v-if="isGroupedOffer"
      :class="[
        'rounded-t-lg',
        'w-[360px] lg:w-[336px]',
        'p-2',
        'flex items-center gap-1.5',
        'bg-white',
      ]"
      @dblclick.stop
    >
      <Button
        variant="white"
        size="circle-40"
        class="search-map-wrapper__nav-btn"
        :disabled="currentOfferIndex === 0"
        @click="prevSlide"
      >
        <Icon
          :svg="IconCaretLeft"
          width="20"
          height="20"
          :fill="currentOfferIndex === 0 ? 'gray-darker' : 'black'"
        />
      </Button>

      <div class="flex-1 overflow-hidden">
        <div
          :class="[
            'w-full',
            'flex gap-1.5',
            'transition-transform duration-300 ease-out',
          ]"
          :style="{ transform: `translateX(calc(-${scrollIndex} * (50% + 3px)))` }"
        >
          <Button
            v-for="(groupedOffer, index) in groupedOffers"
            :key="groupedOffer.hash_id"
            :variant="currentOffer.hash_id === groupedOffer.hash_id ? 'primary' : 'white'"
            size="small"
            :class="[
              'search-map-wrapper__tab',
              currentOffer.hash_id === groupedOffer.hash_id ? '!text-white' : '!text-gray-dark'
            ]"
            @click="currentOfferIndex = index"
            @dblclick.stop
          >
            {{ currencyNoDecimal(getTotalMonthlyPrice(groupedOffer)) }}
          </Button>
        </div>
      </div>

      <Button
        variant="white"
        size="circle-40"
        class="search-map-wrapper__nav-btn"
        :disabled="currentOfferIndex >= maxIndex"
        @click="nextSlide"
        @dblclick.stop
      >
        <Icon
          :svg="IconCaretRight"
          width="20"
          height="20"
          :fill="currentOfferIndex >= maxIndex ? 'gray-darker' : 'black'"
        />
      </Button>
    </div>

    <OfferCard
      :key="currentOffer.hash_id"
      :class="[
        'search-map-card',
        { 'search-map-card--grouped': isGroupedOffer },
      ]"
      :first-offer="deserializedOffer"
      :url="getAggregatedOfferUrl(deserializedOffer)"
      :price-suffix="PriceTypeMode.FULL"
      :width="430"
      :image-ratio="[ 1, 1 ]"
      :carousel-height="130"
      :with-tax-unit="false"
      @dblclick.stop
    >
      <template #actions>
        <FavoriteButton
          :offer-hash-id="currentOffer.hash_id"
          :is-active="isFavorite(currentOffer.hash_id)"
          @change="onFavoriteChange"
        />

        <Button
          variant="circle"
          class="!size-9"
          @click.prevent="emit('close')"
        >
          <Icon
            :svg="IconX"
            width="24"
            height="24"
            fill="gray-darker"
          />
        </Button>
      </template>

      <template #body>
        <div
          :class="[
            'relative',
            'flex flex-col',
            'p-3',
          ]"
        >
          <PromoteType
            v-if="currentOffer.promote_types.length"
            class="z-[1]"
            :promote-type="currentOffer.promote_types[0].code"
          />

          <Typography
            variant="body"
            color="gray-darker"
            class="truncate whitespace-nowrap"
          >
            {{ deserializedOffer.type_infos }} • {{ deserializedOffer.offer_infos }}
          </Typography>

          <OfferPrice
            :offer="deserializedOffer"
            :suffix="PriceTypeMode.FULL"
            :price="deserializedOffer.monthly_total_price"
            :with-tax-unit="false"
          />
        </div>
      </template>
    </OfferCard>
  </div>
</template>

<style lang="scss" scoped>
.search-map-wrapper {
  &__tab {
    @apply shadow-md;

    @apply h-8 w-[calc(50%-3px)] shrink-0;

    @apply p-0;
  }

  &__nav-btn {
    @apply shadow-md;

    @apply size-8;

    @apply p-0;

    @apply shrink-0 flex items-center justify-center;
  }
}

.search-map-card {
  @apply m-0;

  @apply w-[360px] h-[203px] lg:w-[336px];

  &--grouped {
    @apply rounded-t-none;

    :deep(.offer-card__slider) {
      @apply rounded-t-none;
    }
  }
}

:deep(.offer-card__slider img) {
  @apply h-[130px] w-full;

  @apply object-cover;
}

:deep(.flickity-page-dots) {
  @apply z-[1];
}
</style>
